import React, { useEffect } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { TooltipProvider } from '@/components/ui/tooltip';
import { BrowserRouter } from 'react-router-dom';
import { AuthProvider } from '@/hooks/useAuth';
import { DebugProvider } from '@/context/DebugContext';
import { AppProvider } from '@/context/AppContext';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { CoinAnimationProvider } from '@/context/CoinAnimationContext';
import { WDollarAnimationProvider } from '@/context/WDollarAnimationContext';
import { ThemeProvider } from '@/components/ThemeProvider';
import { GoogleMapsProvider } from '@/providers/GoogleMapsProvider';
import { DataCacheProvider } from '@/contexts/DataCacheContext';
import { ViewModeProvider } from '@/contexts/ViewModeContext';
import { preloadCommonIcons } from '@/utils/iconCache';
import { setQueryClientRef } from '@/context/reducers/activityReducers';

// Optimized query client configuration
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 10 * 60_000, // 10 minutes - increased for better performance
      gcTime: 30 * 60 * 1000, // 30 minutes - increased for better caching
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Wire up queryClient ref for W$ conversion cache invalidation
setQueryClientRef(queryClient);

interface AppProvidersProps {
  children: React.ReactNode;
}

export function AppProviders({ children }: AppProvidersProps) {
  // Preload commonly used icons on mount
  useEffect(() => {
    preloadCommonIcons();
  }, []);

  return (
    <ThemeProvider defaultTheme="light" storageKey="activity-tracker-theme">
      <GoogleMapsProvider>
        <QueryClientProvider client={queryClient}>
          <TooltipProvider>
            <BrowserRouter>
              <AuthProvider>
                <DebugProvider>
                  <AppProvider>
                    <ViewModeProvider>
                      <DataCacheProvider>
                        <CoinAnimationProvider>
                          <WDollarAnimationProvider>
                            <ErrorBoundary>
                              {children}
                            </ErrorBoundary>
                          </WDollarAnimationProvider>
                        </CoinAnimationProvider>
                      </DataCacheProvider>
                    </ViewModeProvider>
                  </AppProvider>
                </DebugProvider>
              </AuthProvider>
            </BrowserRouter>
          </TooltipProvider>
        </QueryClientProvider>
      </GoogleMapsProvider>
    </ThemeProvider>
  );
}